import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { deleteAppointmentPermanently, changeAppointmentDoctor } from '../../services/adminService';
import AvailabilityEditor from '../../components/admin/AvailabilityEditor';
import AppointmentCalendar from '../../components/admin/AppointmentCalendar';
import AppointmentList from '../../components/admin/AppointmentList';
import CreateAppointmentModal from '../../components/admin/CreateAppointmentModal';

const DAYS_ES = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
};

export default function AgendaPage() {
  const [doctors, setDoctors]           = useState([]);
  const [selectedDoctor, setSelected]   = useState(null);
  const [availability, setAvailability] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [pagination, setPagination]     = useState({});
  const [view, setView]                 = useState('list'); // 'list' | 'calendar'
  const [filters, setFilters]           = useState({ dateFrom: '', dateTo: '', status: '', page: 1 });
  const [showCreateModal, setCreate]    = useState(false);
  const [loading, setLoading]           = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [error, setError]               = useState('');

  // ── load doctors list
  useEffect(() => {
    api.get('/admin/doctors?limit=100')
      .then(r => setDoctors(r.data.data))
      .catch(() => setError('No se pudo cargar la lista de médicos'));
  }, []);

  // ── load availability when doctor changes
  useEffect(() => {
    if (!selectedDoctor) return;
    setLoadingAvail(true);
    api.get(`/admin/doctors/${selectedDoctor._id}/availability`)
      .then(r => setAvailability(r.data.data.availability))
      .catch(() => setAvailability(null))
      .finally(() => setLoadingAvail(false));
  }, [selectedDoctor]);

  const loadAppointments = useCallback(() => {
    if (!selectedDoctor) return;
    setLoading(true);
    const params = new URLSearchParams({
      page:  filters.page,
      limit: 20,
      ...(filters.status   && { status: filters.status }),
      ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
      ...(filters.dateTo   && { dateTo: filters.dateTo }),
    });
    api.get(`/admin/doctors/${selectedDoctor._id}/appointments?${params}`)
      .then(r => { setAppointments(r.data.data); setPagination(r.data.pagination); })
      .catch(() => setError('No se pudieron cargar las citas'))
      .finally(() => setLoading(false));
  }, [selectedDoctor, filters]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const handleDoctorChange = (e) => {
    const doc = doctors.find(d => d._id === e.target.value) || null;
    setSelected(doc);
    setAppointments([]);
    setAvailability(null);
    setFilters(f => ({ ...f, page: 1 }));
  };

  const handleSaveAvailability = async (newAvail) => {
    try {
      const r = await api.put(`/admin/doctors/${selectedDoctor._id}/availability`, { availability: newAvail });
      setAvailability(r.data.data.availability);
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Error al guardar disponibilidad');
    }
  };

  const handleCancelAppointment = async (id) => {
    if (!window.confirm('¿Cancelar esta cita?')) return;
    await api.delete(`/admin/appointments/${id}`, { data: { reason: 'Cancelado desde panel admin' } });
    loadAppointments();
  };

  // El modal muestra el error si falla; aquí solo se refresca al terminar.
  const handleDeleteAppointment = async (id, reason) => {
    const r = await deleteAppointmentPermanently(id, reason);
    if (r?.message) window.alert(r.message);
    loadAppointments();
  };

  // La cita sale de la lista de este médico al recargar: queda en la del nuevo.
  const handleChangeDoctor = async (id, doctorId, reason) => {
    const r = await changeAppointmentDoctor(id, doctorId, reason);
    if (r?.message) window.alert(r.message);
    loadAppointments();
  };

  const handleEditAppointment = async (id, updates) => {
    await api.put(`/admin/appointments/${id}`, updates);
    loadAppointments();
  };

  const handleCreateAppointment = async (data) => {
    await api.post('/admin/appointments', data);
    setCreate(false);
    loadAppointments();
  };

  return (
    <div className="agenda-page">
      <div className="agenda-header">
        <h1>Gestión de Agenda</h1>
        <button className="btn-primary" onClick={() => setCreate(true)} disabled={!selectedDoctor}>
          + Nueva cita
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Doctor selector */}
      <div className="doctor-selector card">
        <label htmlFor="doc-select">Seleccionar médico</label>
        <select id="doc-select" onChange={handleDoctorChange} defaultValue="">
          <option value="" disabled>-- Elige un médico --</option>
          {doctors.map(d => (
            <option key={d._id} value={d._id}>
              {d.user?.profile?.firstName} {d.user?.profile?.lastName} • {d.specialization}
            </option>
          ))}
        </select>
      </div>

      {selectedDoctor && (
        <>
          {/* Availability editor */}
          <section className="agenda-section card">
            <h2>Disponibilidad semanal</h2>
            {loadingAvail
              ? <p className="text-muted">Cargando...</p>
              : <AvailabilityEditor
                  availability={availability}
                  daysEs={DAYS_ES}
                  onSave={handleSaveAvailability}
                />
            }
          </section>

          {/* Appointments section */}
          <section className="agenda-section">
            <div className="section-toolbar">
              <h2>Citas de {selectedDoctor.user?.profile?.firstName}</h2>
              <div className="view-toggle">
                <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>Lista</button>
                <button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>Calendario</button>
              </div>
            </div>

            {/* Filters */}
            <div className="filters card">
              <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, page: 1 }))} placeholder="Desde" />
              <input type="date" value={filters.dateTo}   onChange={e => setFilters(f => ({ ...f, dateTo:   e.target.value, page: 1 }))} placeholder="Hasta" />
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
                <option value="">Todos los estados</option>
                <option value="scheduled">Programada</option>
                <option value="confirmed">Confirmada</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
                <option value="no-show">No asistió</option>
              </select>
              <button className="btn-secondary" onClick={() => setFilters({ dateFrom: '', dateTo: '', status: '', page: 1 })}>Limpiar</button>
            </div>

            {loading
              ? <p className="text-muted">Cargando citas...</p>
              : view === 'list'
                ? <AppointmentList
                    appointments={appointments}
                    pagination={pagination}
                    onPageChange={(p) => setFilters(f => ({ ...f, page: p }))}
                    onCancel={handleCancelAppointment}
                    onEdit={handleEditAppointment}
                    onDelete={handleDeleteAppointment}
                    doctors={doctors}
                    onChangeDoctor={handleChangeDoctor}
                  />
                : <AppointmentCalendar
                    appointments={appointments}
                    availability={availability}
                    onEdit={handleEditAppointment}
                    onCancel={handleCancelAppointment}
                  />
            }
          </section>
        </>
      )}

      {showCreateModal && (
        <CreateAppointmentModal
          defaultDoctorId={selectedDoctor?._id}
          onSubmit={handleCreateAppointment}
          onClose={() => setCreate(false)}
        />
      )}
    </div>
  );
}
